import React, { ReactNode, useCallback } from 'react';
import cx from 'clsx';

import { wrapModalPromise, WrapModalProps } from 'utils';
import { Button } from 'components';
import { useDeviceTypeIsMobile, useSwitch } from '@/hooks';

import './index.css';

interface InfoModalProps {
  title?: ReactNode;
  content?: ReactNode | ((hide: () => void) => ReactNode);
  hideConfirmBtn?: boolean;
  confirmBtnText?: ReactNode;
  onConfirmClick?(): Awaited<any>;
  closeable?: boolean;
  className?: string;
  titleClassName?: string;
  contentClassName?: string;
  confirmButtonClassName?: string;
  closeIcon?: ReactNode;
}

export const InfoModal = ({
  title,
  content,
  hideConfirmBtn = false,
  confirmBtnText = 'OK',
  onConfirmClick,
  closeable = false,
  hide,
  onResolve,
  onReject,
  className,
  titleClassName,
  contentClassName,
  confirmButtonClassName,
}: InfoModalProps & Partial<WrapModalProps>) => {
  const { on, turnOn, turnOff } = useSwitch();
  const isMobile = useDeviceTypeIsMobile();

  const handleConfirmClick = useCallback(async () => {
    try {
      turnOn();
      await onConfirmClick?.();
      onResolve?.();
      hide?.();
    } finally {
      turnOff();
    }
  }, [onConfirmClick, onResolve, hide, turnOff, turnOn]);

  return (
    <div className={cx('db-infoModal', isMobile && 'isMobile', className)}>
      {title && <div className={cx('db-infoModal-title', titleClassName)}>{title}</div>}
      {content && (
        <div className={cx('db-infoModal-content', contentClassName)}>
          {typeof content === 'function' ? content(hide!) : content}
        </div>
      )}
      {!hideConfirmBtn && (
        <div className="db-infoModal-btn">
          <Button
            type="primary"
            onClick={handleConfirmClick}
            ghost={isMobile}
            loading={on}
            className={cx('db-infoModal-confirmBtn', confirmButtonClassName)}>
            {confirmBtnText}
          </Button>
        </div>
      )}
    </div>
  );
};

export const info = wrapModalPromise<InfoModalProps>(InfoModal, {
  containerClassName: 'db-infoModal-container',
});
